import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useAudit } from "./AuditContext";
import { PresenterFloorShadow } from "./PresenterFloorShadow";
import { useIsMobileAudit } from "./useIsMobileAudit";

/**
 * Rehearsal Room · Room 07 — REFLECTION (3 free-text + 5 required choice)
 * ========================================================================
 * The full reflection section lives in this single room. Layout, presenter
 * placement/poses/scale, bubble behaviour, textarea styling, walk-off exit
 * and transitions are unchanged from the original "perfect" Q1 version.
 * A `questionIndex` local state advances through eight questions within the
 * same card. Global Continue is enabled only on the last question when it
 * has content.
 *
 * Questions:
 *   Q1 (text):                  What did you notice about yourself while going through the audit?
 *   Q2 (text):                  Which part of your communication would you most like to explore or
 *                               improve next?
 *   Q3 (text):                  Is there anything about your communication, your work, or the
 *                               situations you speak in that you think would help us understand your
 *                               results better?
 *   Q4 (choice):                If you had an important presentation coming up, how open would you
 *                               be to investing in professional support to prepare for it?
 *   Q5 (choice):                When is your next important presentation?
 *   Q6 (choice + other):        What are you preparing for?
 *   Q7 (multi-choice, max 2 + other): What would make the biggest difference for you right now?
 *   Q8 (choice):                Who would normally fund this kind of support?
 *
 * Q1-Q3 land in the shared global reflections bucket under distinct keys
 * (`reflection_q1/2/3`) so the Pattern Engine can read each individually.
 * Q4-Q8 are DELIBERATELY kept OUT of that bucket and out of `answers` —
 * they persist as dedicated top-level AuditContext fields (`coachingSupport
 * Interest`/`nextPresentationTiming`/`presentationContext`/`presentation
 * ContextOther`/`supportPriorities`/`supportPrioritiesOther`/`fundingSource`,
 * see AuditContext.jsx) so they can NEVER reach Audit analysis, report
 * content, exercise mapping or the recommendation — they are user-provided
 * commercial context only, and do not imply marketing consent (see
 * ResultsRoom.jsx/patternEngine.js for where they are forwarded, entirely
 * separately from `patternRead`).
 */

const EASE = [0.16, 1, 0.3, 1];
const BASE_PRESENTER_HEIGHT = 104;
const MAX_SUPPORT_PRIORITIES = 2;

const QUESTIONS = [
  {
    type: "text",
    key: "reflection_q1",
    label: "Room 07 · Question 1 of 8",
    text:
      "What did you notice about yourself while going through the audit?",
    intro: "Take a moment. Write freely — a line or a paragraph.",
  },
  {
    type: "text",
    key: "reflection_q2",
    label: "Room 07 · Question 2 of 8",
    text:
      "Which part of your communication would you most like to explore or improve next?",
    intro: "Now — where would you most like to grow next?",
  },
  {
    type: "text",
    key: "reflection_q3",
    label: "Room 07 · Question 3 of 8",
    text:
      "Is there anything about your communication, your work, or the situations you speak in that you think would help us understand your results better?",
    intro: "One last note — anything else that would help us read your results?",
  },
  {
    type: "choice",
    key: "coaching_support_interest",
    label: "Room 07 · Question 4 of 8",
    text: "If you had an important presentation coming up, how open would you be to investing in professional support to prepare for it?",
    intro: "One more short question before we look at your feedback.",
    options: [
      { id: "seriously_consider", label: "I\u2019d seriously consider professional support" },
      { id: "open_to_exploring", label: "I\u2019d be open to exploring the options" },
      { id: "understand_how_it_could_help", label: "I\u2019d first want to understand how it could help" },
      { id: "prefer_prepare_alone", label: "I\u2019d prefer to prepare on my own" },
    ],
  },
  {
    type: "choice",
    key: "next_presentation_timing",
    label: "Room 07 · Question 5 of 8",
    text: "When is your next important presentation?",
    intro: "Next.",
    options: [
      { id: "within_1_month", label: "Within the next month" },
      { id: "in_1_2_months", label: "In 1\u20132 months" },
      { id: "in_2_3_months", label: "In 2\u20133 months" },
      { id: "more_than_3_months", label: "More than 3 months from now" },
      { id: "no_plan_yet", label: "I do not have one planned yet" },
    ],
  },
  {
    type: "choice_with_other",
    key: "presentation_context",
    label: "Room 07 · Question 6 of 8",
    text: "What are you preparing for?",
    intro: "Next — a bit more about the situation itself.",
    otherPlaceholder: "Please describe what you're preparing for...",
    options: [
      { id: "conference_keynote", label: "Conference / keynote" },
      { id: "board_leadership_presentation", label: "Board / leadership presentation" },
      { id: "client_sales_presentation", label: "Client / sales presentation" },
      { id: "investor_pitch", label: "Investor / pitch" },
      { id: "internal_team_presentation", label: "Internal team presentation" },
      { id: "media_camera", label: "Media / camera" },
      { id: "other", label: "Other" },
    ],
  },
  {
    type: "multi_choice_with_other",
    key: "support_priorities",
    label: "Room 07 · Question 7 of 8",
    text: "What would make the biggest difference for you right now?",
    intro: "You can choose up to two.",
    otherPlaceholder: "Please describe what would help...",
    options: [
      { id: "structure_message", label: "Structure my message more clearly" },
      { id: "speak_with_confidence", label: "Speak with more confidence/presence" },
      { id: "use_voice_effectively", label: "Use my voice more effectively" },
      { id: "use_body_space", label: "Use body/space more intentionally" },
      { id: "handle_difficult_questions", label: "Handle difficult questions" },
      { id: "engage_audience", label: "Engage the audience" },
      { id: "feel_natural_in_english", label: "Feel more natural in English" },
      { id: "other", label: "Other" },
    ],
  },
  {
    type: "choice",
    key: "funding_source",
    label: "Room 07 · Question 8 of 8",
    text: "Who would normally fund this kind of support?",
    intro: "Last one.",
    options: [
      { id: "self_funded", label: "I would personally" },
      { id: "company_funded", label: "My company" },
      { id: "depends", label: "It depends" },
      { id: "not_sure_yet", label: "I'm not sure yet" },
    ],
  },
];

// Presenter anchors (viewport %). footY > 100 lets the feet fall below the
// viewport so we frame the presenter from the knees up.
const OFFSTAGE_ENTRY = { cx: 112, footY: 106, scale: 1.0 }; // off-stage right
const STAGE_RIGHT    = { cx: 82,  footY: 106, scale: 1.0 };
const OFFSTAGE_LEFT  = { cx: -12, footY: 106, scale: 1.0 };

// Presenter closing beats after all 3 questions are answered — played
// before Continue unlocks and moves to the feedback section.
const CLOSING_STEPS = [
  "Thank you. That gives me useful context.",
  "I\u2019ve got what I need to connect your choices with what you noticed about yourself.",
  "Now let\u2019s look at your feedback.",
];

const anchorToStyle = (a) => ({
  left:   `${a.cx}%`,
  bottom: `${100 - a.footY}%`,
  height: `${BASE_PRESENTER_HEIGHT * a.scale}%`,
});

export function RehearsalRoomReflection() {
  const {
    answers,
    setRoomAnswer,
    setCanProceed,
    setOverrideMessage,
    setBeforeNext,
    setReflection,
    selectedPresenter,
    overrideMessage,
    next,
    coachingSupportInterest,
    nextPresentationTiming,
    setCoachingSupportInterest,
    setNextPresentationTiming,
    presentationContext,
    presentationContextOther,
    setPresentationContext,
    setPresentationContextOther,
    supportPriorities,
    supportPrioritiesOther,
    toggleSupportPriority,
    setSupportPrioritiesOther,
    fundingSource,
    setFundingSource,
  } = useAudit();

  // One bucket per question so answers persist as the user advances.
  const q1 = answers.reflection?.q1 ?? "";
  const q2 = answers.reflection?.q2 ?? "";
  const q3 = answers.reflection?.q3 ?? "";
  const questionIndex = answers.reflection?.questionIndex ?? 0;
  const closingStarted = answers.reflection?.closingStarted === true;
  const closingStep    = answers.reflection?.closingStep ?? 0;
  const closingDone    = closingStarted && closingStep >= CLOSING_STEPS.length;

  const currentQuestion = QUESTIONS[questionIndex];

  // Single-select choice value by question key (covers "choice" and
  // "choice_with_other" — "multi_choice_with_other" uses the
  // `supportPriorities` array directly, handled separately below).
  const CHOICE_VALUES = {
    coaching_support_interest: coachingSupportInterest,
    next_presentation_timing: nextPresentationTiming,
    presentation_context: presentationContext,
    funding_source: fundingSource,
  };
  const OTHER_TEXT_VALUES = {
    presentation_context: presentationContextOther,
    support_priorities: supportPrioritiesOther,
  };
  const choiceValue = CHOICE_VALUES[currentQuestion.key] ?? null;
  const otherText = OTHER_TEXT_VALUES[currentQuestion.key] ?? "";

  // Whether a given question already has a valid, complete answer —
  // used for both the "advance/Continue" gate and the status caption.
  // "Other" is only complete once its paired free-text field is filled
  // in (mirrors the backend's own cross-field validation).
  const isAnswered = (question, idx) => {
    if (question.type === "text") {
      return ([q1, q2, q3][idx] ?? "").trim().length > 0;
    }
    if (question.type === "choice") {
      return !!CHOICE_VALUES[question.key];
    }
    if (question.type === "choice_with_other") {
      const value = CHOICE_VALUES[question.key];
      if (!value) return false;
      return value !== "other" || (OTHER_TEXT_VALUES[question.key] || "").trim().length > 0;
    }
    if (question.type === "multi_choice_with_other") {
      const selection = supportPriorities || [];
      if (selection.length === 0) return false;
      return !selection.includes("other") || (supportPrioritiesOther || "").trim().length > 0;
    }
    return false;
  };

  const answered = isAnswered(currentQuestion, questionIndex);
  // Only meaningful for the "text" question type — word count caption.
  const currentTextNotes = currentQuestion.type === "text" ? ([q1, q2, q3][questionIndex] ?? "") : "";

  const [entered, setEntered] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [pose, setPose] = useState("walking");
  const [flip, setFlip] = useState(false);
  const isMobile = useIsMobileAudit();
  // Mobile-only: hide the coaching bubble the moment the user starts
  // typing (textarea or the "other" free-text input focused), so it
  // never floats over the input/keyboard area. Only reset when they
  // explicitly move on (Next Question / new question), never on blur —
  // matches "next bubble only appears after Next Question is tapped".
  const [typingFocused, setTypingFocused] = useState(false);
  useEffect(() => { setTypingFocused(false); }, [questionIndex]);
  // Mobile-only: brief highlight flash on the tapped option before the
  // auto-advance in `handleChoiceSelect` actually moves to the next
  // question/closing beat, so the jump reads as an intentional response
  // to the tap rather than an abrupt cut.
  const [flashingChoiceId, setFlashingChoiceId] = useState(null);
  useEffect(() => { setFlashingChoiceId(null); }, [questionIndex]);
  const CHOICE_FLASH_MS = 220;

  // Presenter walks in from OFFSTAGE right → STAGE_RIGHT. He moves LEFT
  // into the room, so the walking sprite is flipped to face his direction.
  useEffect(() => {
    setPose("walking");
    setFlip(true);
    const t = setTimeout(() => {
      setPose("thinking");
      setFlip(false);
      setEntered(true);
    }, 1200);
    return () => clearTimeout(t);
  }, []);

  const inClosing = entered && closingStarted && !closingDone;

  // Coaching bubble + Continue gate.
  // Continue starts the closing beats on the last question, and only
  // advances to feedback once all closing beats have been seen.
  useEffect(() => {
    if (!entered) { setOverrideMessage(""); setCanProceed(false); return; }
    if (inClosing) {
      setOverrideMessage(CLOSING_STEPS[closingStep]);
      setCanProceed(false);
      return;
    }
    if (closingDone) {
      setOverrideMessage(CLOSING_STEPS[CLOSING_STEPS.length - 1]);
      setCanProceed(true);
      return;
    }
    setOverrideMessage(currentQuestion.intro);
    const onLast = questionIndex === QUESTIONS.length - 1;
    setCanProceed(onLast && answered);
  }, [entered, inClosing, closingStep, closingDone, questionIndex, answered,
      currentQuestion.intro, setOverrideMessage, setCanProceed]);

  // Continue interceptor — first press (last question answered) starts the
  // closing beats; once they're done, Continue triggers the walk-off exit
  // to feedback.
  const lastQuestion = QUESTIONS[QUESTIONS.length - 1];
  const lastAnswered = isAnswered(lastQuestion, QUESTIONS.length - 1);
  useEffect(() => {
    setBeforeNext(() => {
      if (closingDone) {
        setPose("walking");
        setFlip(true); // moving left → flip
        setExiting(true);
        setTimeout(() => { setBeforeNext(null); next(); }, 1200);
        return false;
      }
      if (!closingStarted && lastAnswered) {
        setRoomAnswer("reflection", { closingStarted: true, closingStep: 0 });
        return false;
      }
      return false;
    });
    return () => setBeforeNext(null);
  }, [closingDone, closingStarted, lastAnswered, setBeforeNext, setRoomAnswer, next]);

  const targetAnchor = exiting ? OFFSTAGE_LEFT
                     : !entered ? OFFSTAGE_ENTRY
                     : STAGE_RIGHT;

  const presenter = selectedPresenter;
  const poseFile = presenter?.poses?.[pose] || presenter?.poses?.neutral;
  const presenterSrc = presenter ? `${presenter.avatarBase}/${poseFile}` : "";

  // Bubble anchor — always LEFT of presenter's head (he's on the right side).
  const cx = targetAnchor.cx;
  const headYPct = Math.max(3, targetAnchor.footY - BASE_PRESENTER_HEIGHT * targetAnchor.scale - 1);
  const bubbleStyle = {
    top: `${headYPct}%`,
    left: `calc(${cx - 4}% - 350px)`,
    transform: "translateX(-100%)",
    zIndex: 100,
    maxWidth: 400,
  };

  const answerKey = ["q1", "q2", "q3"][questionIndex];

  const handleTextChange = (e) => {
    const value = e.target.value;
    setRoomAnswer("reflection", { [answerKey]: value });
    setReflection(currentQuestion.key, {
      question: currentQuestion.text,
      notes: value,
    });
  };

  // Q4-Q8 (choice-family) — deliberately NOT written to `setReflection`/
  // `answers` (see the module docstring above); persisted only via the
  // dedicated AuditContext setters, kept fully outside Audit analysis/
  // report content.
  const handleChoiceSelect = (optionId) => {
    if (currentQuestion.key === "coaching_support_interest") setCoachingSupportInterest(optionId);
    else if (currentQuestion.key === "next_presentation_timing") setNextPresentationTiming(optionId);
    else if (currentQuestion.key === "presentation_context") setPresentationContext(optionId);
    else if (currentQuestion.key === "funding_source") setFundingSource(optionId);
    if (isMobile && optionId !== "other") {
      // Flash the tapped option first, then advance a beat later so the
      // jump reads as a direct response to the tap rather than an
      // instant cut to a different screen.
      setFlashingChoiceId(optionId);
      setTimeout(() => {
        if (!onLast) {
          // Picking a plain option auto-advances straight to the next
          // question instead of requiring a separate "Next question" tap.
          setTypingFocused(false);
          setRoomAnswer("reflection", { questionIndex: questionIndex + 1 });
        } else {
          // Question 8 (last) — instead of requiring a first "Continue"
          // tap just to kick off the closing beats, start them immediately
          // once an answer is picked, matching the same auto-advance feel.
          setRoomAnswer("reflection", { closingStarted: true, closingStep: 0 });
        }
      }, CHOICE_FLASH_MS);
    }
  };

  const handleMultiChoiceToggle = (optionId) => {
    toggleSupportPriority(optionId);
  };

  const handleOtherTextChange = (e) => {
    const value = e.target.value;
    if (currentQuestion.key === "presentation_context") setPresentationContextOther(value);
    else if (currentQuestion.key === "support_priorities") setSupportPrioritiesOther(value);
  };

  const advanceToNextQuestion = () => {
    if (!answered) return;
    setTypingFocused(false);
    setRoomAnswer("reflection", { questionIndex: questionIndex + 1 });
  };

  const wordCount = currentTextNotes.trim().length === 0
    ? 0
    : currentTextNotes.trim().split(/\s+/).length;

  const onLast = questionIndex === QUESTIONS.length - 1;
  const showOtherInput =
    (currentQuestion.type === "choice_with_other" && choiceValue === "other") ||
    (currentQuestion.type === "multi_choice_with_other" && (supportPriorities || []).includes("other"));

  const otherTextInput = (
    <input
      type="text"
      value={otherText}
      onChange={handleOtherTextChange}
      onFocus={() => isMobile && setTypingFocused(true)}
      placeholder={currentQuestion.otherPlaceholder || "Please specify..."}
      className="w-full mt-3 px-4 py-3 font-serif text-base text-[#1B1B18] placeholder:text-[#8a7455] focus:outline-none focus:ring-2 focus:ring-[#c25a3a]"
      style={{
        background: "rgba(255,255,255,0.75)",
        border: "1px solid rgba(90,60,25,0.20)",
        borderRadius: 12,
      }}
      data-testid={`reflection-other-textinput-${questionIndex}`}
    />
  );

  return (
    <div className="absolute inset-0 overflow-hidden" data-testid="rehearsal-room-reflection" style={{ background: "#141310" }}>
      {/* Background */}
      <div aria-hidden="true" className="absolute inset-0"
        style={{ backgroundImage: "url(/audit/bg_reflection.webp)", backgroundSize: "cover", backgroundPosition: "center" }} />

      {/* Subtle scrim */}
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none"
        style={{ background: "rgba(10,8,5,0.15)", zIndex: 5 }} />

      {/* ---------- Reflection card (LEFT side) --------------------------- */}
      <motion.div
        className="absolute"
        initial={{ opacity: 0, x: -30 }}
        animate={{ opacity: entered ? 1 : 0, x: entered ? 0 : -30 }}
        transition={{ duration: 0.55, ease: EASE, delay: entered ? 0.2 : 0 }}
        style={{ left: "6%", top: "22%", width: "44%", zIndex: 20 }}
        data-testid="reflection-card"
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={currentQuestion.key}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="bg-[rgba(255,249,234,0.94)] p-8 md:p-10 shadow-[0_20px_50px_rgba(0,0,0,0.35)] overflow-y-auto"
            style={{ borderRadius: 20, border: "1px solid rgba(90,60,25,0.18)", maxHeight: "72vh" }}
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-terracotta mb-3"
              data-testid="reflection-progress">
              {currentQuestion.label}
            </p>
            <p className="font-serif text-[#1B1B18] text-2xl md:text-[28px] leading-snug mb-6"
              data-testid={`reflection-question-${questionIndex}`}>
              {currentQuestion.text}
            </p>
            {currentQuestion.type === "text" ? (
              <textarea
                value={currentTextNotes}
                onChange={handleTextChange}
                onFocus={() => isMobile && setTypingFocused(true)}
                placeholder="Write freely — one line or a paragraph. This shapes the final map."
                className="w-full min-h-[160px] p-4 font-serif text-lg text-[#1B1B18] placeholder:text-[#8a7455] resize-y focus:outline-none focus:ring-2 focus:ring-[#c25a3a]"
                style={{
                  background: "rgba(255,255,255,0.75)",
                  border: "1px solid rgba(90,60,25,0.20)",
                  borderRadius: 12,
                  lineHeight: 1.55,
                }}
                data-testid={`reflection-textarea-${questionIndex}`}
              />
            ) : currentQuestion.type === "multi_choice_with_other" ? (
              <>
                <div className="space-y-2.5" data-testid={`reflection-choices-${questionIndex}`}>
                  {currentQuestion.options.map((opt) => {
                    const selected = (supportPriorities || []).includes(opt.id);
                    const maxReached = (supportPriorities || []).length >= MAX_SUPPORT_PRIORITIES;
                    const disabled = !selected && maxReached;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => handleMultiChoiceToggle(opt.id)}
                        disabled={disabled}
                        data-testid={`reflection-choice-${questionIndex}-${opt.id}`}
                        className="w-full text-left px-4 py-3 font-serif text-base md:text-lg transition-colors disabled:cursor-not-allowed"
                        style={{
                          borderRadius: 12,
                          border: selected ? "1.5px solid #c25a3a" : "1px solid rgba(90,60,25,0.20)",
                          background: selected ? "rgba(194,90,58,0.12)" : "rgba(255,255,255,0.55)",
                          color: "#1B1B18",
                          opacity: disabled ? 0.4 : 1,
                        }}
                        aria-pressed={selected}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#8a7455]"
                  data-testid="reflection-multi-select-hint">
                  Choose up to {MAX_SUPPORT_PRIORITIES} · {(supportPriorities || []).length} selected
                </p>
                {showOtherInput && otherTextInput}
              </>
            ) : (
              <>
                <div className="space-y-2.5" data-testid={`reflection-choices-${questionIndex}`}>
                  {currentQuestion.options.map((opt) => {
                    const selected = choiceValue === opt.id;
                    const flashing = flashingChoiceId === opt.id;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => handleChoiceSelect(opt.id)}
                        data-testid={`reflection-choice-${questionIndex}-${opt.id}`}
                        className="w-full text-left px-4 py-3 font-serif text-base md:text-lg transition-all duration-150"
                        style={{
                          borderRadius: 12,
                          border: flashing ? "1.5px solid #da983c" : selected ? "1.5px solid #c25a3a" : "1px solid rgba(90,60,25,0.20)",
                          background: flashing
                            ? "linear-gradient(180deg, #E9C88A 0%, #B9832C 100%)"
                            : selected ? "rgba(194,90,58,0.12)" : "rgba(255,255,255,0.55)",
                          color: "#1B1B18",
                          transform: flashing ? "scale(1.03)" : "scale(1)",
                          boxShadow: flashing ? "0 6px 16px rgba(184,131,44,0.4)" : "none",
                        }}
                        aria-pressed={selected}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                {showOtherInput && otherTextInput}
              </>
            )}
            <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#8a7455]"
                data-testid="reflection-status">
                {currentQuestion.type === "text"
                  ? (!answered
                      ? (onLast ? "Write something to continue" : "Write something to advance")
                      : `${wordCount} words · ready`)
                  : currentQuestion.type === "multi_choice_with_other"
                  ? (!answered
                      ? (onLast ? "Select up to 2 options to continue" : "Select up to 2 options to advance")
                      : "Selected · ready")
                  : (!answered
                      ? (onLast ? "Select an option to continue" : "Select an option to advance")
                      : "Selected · ready")}
              </p>
              {!onLast && (
                <button
                  type="button"
                  onClick={advanceToNextQuestion}
                  disabled={!answered}
                  className="font-mono text-[11px] uppercase tracking-[0.24em] px-5 py-2 border transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{
                    color: "#1B1B18",
                    borderColor: "rgba(90,60,25,0.35)",
                    background: !answered
                      ? "transparent"
                      : "rgba(218,152,60,0.22)",
                    borderRadius: 999,
                  }}
                  data-testid="reflection-next-question-btn"
                >
                  Next question →
                </button>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </motion.div>

      {/* ---------- Presenter (RIGHT side) -------------------------------- */}
      {presenter && (
        <motion.div
          className="absolute"
          initial={false}
          animate={{
            left:   anchorToStyle(targetAnchor).left,
            bottom: anchorToStyle(targetAnchor).bottom,
            height: anchorToStyle(targetAnchor).height,
            opacity: exiting ? 0 : 1,
          }}
          transition={{ duration: exiting ? 1.1 : 1.2, ease: EASE }}
          style={{
            zIndex: 30,
            transform: "translateX(-50%)",
            aspectRatio: "1122 / 1402",
            pointerEvents: "none",
          }}
          data-testid="reflection-presenter"
        >
          <PresenterFloorShadow />
          <img
            src={presenterSrc}
            alt={presenter.name}
            draggable={false}
            loading="eager"
            decoding="async"
            fetchPriority="high"
            className="select-none block"
            style={{
              height: "100%",
              width: "100%",
              objectFit: "contain",
              transform: `scaleX(${flip ? -1 : 1})`,
              transition: "transform 350ms ease",
              filter: "brightness(0.95) sepia(0.1)",
              WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, black 5%, black 95%, transparent 100%)",
              maskImage:       "linear-gradient(to bottom, transparent 0%, black 5%, black 95%, transparent 100%)",
            }}
            data-testid={`reflection-presenter-img-${pose}`}
          />
        </motion.div>
      )}

      {/* ---------- Corner brand mark ------------------------------------- */}
      <div className="absolute right-6 top-5 flex flex-col items-end pointer-events-none" style={{ zIndex: 50 }}>
        <p className="font-serif italic text-paper text-lg md:text-2xl leading-none tracking-wide drop-shadow-[0_2px_6px_rgba(0,0,0,0.55)]">
          InterAct Globally
        </p>
        <p className="font-hand text-mustard text-base md:text-xl leading-tight mt-1 drop-shadow-[0_2px_6px_rgba(0,0,0,0.55)]">
          the reflection room
        </p>
      </div>

      {/* ---------- Speech bubble (follows presenter) --------------------- */}
      <AnimatePresence initial={false}>
        {entered && overrideMessage && !(isMobile && typingFocused) && (
          <motion.div
            key={overrideMessage}
            initial={{ opacity: 0, y: 8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="absolute"
            style={bubbleStyle}
            data-testid="reflection-bubble"
          >
            <div className="relative bg-white text-[#1B1B18] px-6 py-4 shadow-[0_16px_40px_rgba(0,0,0,0.4)]" style={{ borderRadius: 26 }}>
              <p className="font-serif text-lg leading-snug">{overrideMessage}</p>
              <span aria-hidden="true" className="absolute"
                style={{ right: 24, bottom: -16, width: 0, height: 0,
                  borderLeft: "16px solid transparent", borderRight: "10px solid transparent",
                  borderTop: "20px solid #ffffff", filter: "drop-shadow(0 4px 4px rgba(0,0,0,0.15))" }} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* ---------- Closing beats tap catcher ------------------------------ */}
      {inClosing && (
        <button
          type="button"
          onClick={() => setRoomAnswer("reflection", { closingStep: closingStep + 1 })}
          data-testid="reflection-closing-advance"
          aria-label="Continue"
          className="absolute inset-0 cursor-pointer bg-transparent"
          style={{ zIndex: 88 }}
        >
          <span aria-hidden="true"
            className="absolute bottom-24 left-1/2 -translate-x-1/2 font-mono text-[10px] uppercase tracking-[0.28em] text-paper/70 animate-pulse">
            tap to continue &nbsp;·&nbsp; {closingStep + 1} / {CLOSING_STEPS.length}
          </span>
        </button>
      )}
    </div>
  );
}

export default RehearsalRoomReflection;
